import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';

import * as apiCalls from '../../../api/dlgw/compat/datalabs/apiCalls';
import { mapFromDatalabRequestDtoFilters } from '../model/datalab-api-mapping';
import { getFilterDefaultValues, getFilterSettings } from '../model/filters.model';
import { DatalabFormValues, DatalabRequestDtoFilter, FilterSettings } from '../types';
import { useCurrentDatalabScreen } from './useCurrentDatalabScreen';

/**
 * Validates if the mapped filters are supported by the current screen
 *
 * @param mappedFilters - The filters mapped from the API response
 * @param defaults - The default filter values for the current screen
 * @returns An object containing validation result and optional error message
 */
const validateMappedFilters = (
  mappedFilters: DatalabFormValues,
  defaults: ReturnType<typeof getFilterDefaultValues>
): { isValid: boolean; errorMessage?: string } => {
  const isOfferingTypeValid = mappedFilters.offeringTypeInternal?.every(offeringType =>
    defaults.offeringTypeInternal?.includes(offeringType)
  );

  if (!isOfferingTypeValid) {
    return {
      isValid: false,
      errorMessage: 'An offering type in the query is not supported',
    };
  }

  const settings: FilterSettings = getFilterSettings();
  const countriesContainOnlyUSA = mappedFilters.countries?.every(country => country === 'USA');

  if (!settings.showInternational && mappedFilters.countries?.length && !countriesContainOnlyUSA) {
    return {
      isValid: false,
      errorMessage: 'Only USA in the query is not supported',
    };
  }

  return { isValid: true };
};

/**
 * Hook that manages AI generated filters state and operations.
 * Uses Formik context to access and update filters directly.
 *
 * @param onError - Optional callback function to handle errors
 * @returns Object containing loading state and function to generate filters
 */
export const useAIGeneratedFilters = (onError?: (error: Error) => void) => {
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const screen = useCurrentDatalabScreen();

  const formik = useFormikContext<DatalabFormValues>();

  /**
   * Generates filters based on a natural language query
   * @param query - The natural language query to process
   */
  const generateFiltersFromQuery = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        return;
      }

      setIsLoading(true);

      try {
        if (!screen) {
          return;
        }

        const response = await apiCalls.fetchAIGeneratedFilters(query, lastQuery || undefined);

        setLastQuery(query);

        const defaults = getFilterDefaultValues({}, screen, {});

        const mappedFilters = mapFromDatalabRequestDtoFilters(
          response.data as DatalabRequestDtoFilter,
          formik.values,
          {
            date: {
              start: defaults.startDateValue,
              end: defaults.endDateValue,
              type: defaults.typeDateValue,
            },
            offeringType: defaults.offeringTypeInternal,
            offeringTypeInternal: defaults.offeringTypeInternal,
            marketCap: {
              min: defaults.marketCapValueMin,
              max: defaults.marketCapValueMax,
            },
            offeringSize: {
              min: defaults.offeringSizeValueMin,
              max: defaults.offeringSizeValueMax,
            },
            sellingShareholderPct: {
              min: defaults.sellingShareholderPctMin,
              max: defaults.sellingShareholderPctMax,
            },
            sector: defaults.bySectorsValues,
            customSector: defaults.byCustomSectorsValues,
            underwriters: defaults.underwritersValues,
            sponsors: defaults.sponsorsValues,
            advisories: defaults.advisoriesValues,
            spac: defaults.spac,
            funds: defaults.fundsValues,
            advancedQuery: defaults.advancedQuery,
            useCustomSectors: defaults.useCustomSectors,
            countries: defaults.countries,
          }
        );

        const validation = validateMappedFilters(mappedFilters, defaults);

        if (!validation.isValid) {
          onError?.(new Error(validation.errorMessage || 'Filter validation failed'));
          return;
        }

        formik.setValues(mappedFilters);

        formik.submitForm();
      } catch (error) {
        setLastQuery(query);
        if (onError) {
          onError(error instanceof Error ? error : new Error('Unknown error occurred'));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [screen, lastQuery, formik, onError]
  );

  return {
    isLoading,
    generateFiltersFromQuery,
    lastQuery,
  };
};

export default useAIGeneratedFilters;
